import { useMutation } from '@apollo/client';
import { produce } from 'immer';
import React, { useState } from 'react';
import { v4 } from 'uuid';

import {
  QUERY_ROOT_ID,
  cacheUtils,
  useCompleteFragment,
} from '@eluve/apollo-client';
import { AppointmentTag } from '@eluve/blocks';
import {
  Button,
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Icon,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Stack,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';
import { formatAppointmentTagLabel } from '@eluve/utils';

import {
  appointmentTagOptionsFragment,
  appointmentTagsFragment,
} from './operations';

const addAppointmentTagMutation = graphql(`
  mutation addAppointmentTag(
    $appointmentId: uuid!
    $tagId: uuid!
    $tenantId: uuid!
  ) {
    insertAppointmentTagsOne(
      object: {
        appointmentId: $appointmentId
        tagId: $tagId
        tenantId: $tenantId
      }
    ) {
      __typename
      id
    }
  }
`);

const removeAppointmentTagMutation = graphql(`
  mutation removeAppointmentTag($tenantId: uuid!, $appointmentTagId: uuid!) {
    deleteAppointmentTagsByPk(tenantId: $tenantId, id: $appointmentTagId) {
      __typename
      id
    }
  }
`);

export interface AppointmentTagsProps {
  tenantId: string;
  appointmentId: string;
}

export const AppointmentTags: React.FC<AppointmentTagsProps> = ({
  tenantId,
  appointmentId,
}) => {
  const toast = useToast();
  const [tagsOpen, setTagsOpen] = useState(false);

  const tagOptions = useCompleteFragment({
    fragment: appointmentTagOptionsFragment,
    key: {
      id: QUERY_ROOT_ID,
    },
  });

  const appointmentTags = useCompleteFragment({
    fragment: appointmentTagsFragment,
    key: {
      id: appointmentId,
    },
  });

  const [addAppointmentTag] = useMutation(addAppointmentTagMutation, {
    onError: () => toast.error('Failed to add tag.'),
    optimisticResponse: {
      insertAppointmentTagsOne: {
        __typename: 'AppointmentTags' as const,
        id: v4(),
      },
    },
  });

  const [removeAppointmentTag] = useMutation(removeAppointmentTagMutation, {
    onError: () => toast.error('Failed to remove tag.'),
    optimisticResponse: ({ appointmentTagId }) => ({
      deleteAppointmentTagsByPk: {
        __typename: 'AppointmentTags' as const,
        id: appointmentTagId,
      },
    }),
  });

  const handleAddAppointmentTag = (tagId: string) => {
    const tagOption = (tagOptions.appointmentTagOptions ?? []).find(
      (t) => t.id === tagId,
    );

    if (!tagOption) {
      return;
    }

    addAppointmentTag({
      variables: {
        appointmentId,
        tagId,
        tenantId,
      },
      update: (_cache, { data }) => {
        cacheUtils.updateFragment(
          {
            fragment: appointmentTagsFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }
            return produce(existing, (draft) => {
              const now = new Date().toISOString();
              draft.tags.push({
                __typename: 'AppointmentTags' as const,
                id: data?.insertAppointmentTagsOne?.id ?? v4(),
                createdAt: now,
                updatedAt: now,
                resolvedAt: null,
                isSelfResolved: null,
                tag: {
                  __typename: 'AppointmentTagOptions',
                  id: tagId,
                  tag: tagOption.tag,
                  isSystemTag: tagOption.isSystemTag ?? false,
                  isResolvable: tagOption.isResolvable ?? false,
                },
              });
            });
          },
        );
      },
    });
  };

  const handleRemoveAppointmentTag = (appointmentTagId: string) => {
    removeAppointmentTag({
      variables: {
        tenantId,
        appointmentTagId,
      },
      update: (_cache) => {
        cacheUtils.updateFragment(
          {
            fragment: appointmentTagsFragment,
            key: {
              id: appointmentId,
            },
          },
          (existing) => {
            if (!existing) {
              return existing;
            }

            return {
              ...existing,
              tags: existing.tags.filter((t) => t.id !== appointmentTagId),
            };
          },
        );
      },
    });
  };

  const tags = appointmentTags?.tags ?? [];

  const availableTagOptions = (tagOptions.appointmentTagOptions ?? []).filter(
    (t) => !tags.some((tag) => tag.tag.id === t.id) && !t.isSystemTag,
  );

  return (
    <Stack wrap gap={2} direction="horizontal">
      {tags.map((tag) => (
        <AppointmentTag
          key={tag.id}
          onRemoveClick={() => handleRemoveAppointmentTag(tag.id)}
          tag={{
            id: tag.id,
            tag: tag.tag.tag,
            isSystemTag: tag.tag.isSystemTag,
            isResolvable: tag.tag.isResolvable,
            createdAt: tag.createdAt,
            updatedAt: tag.updatedAt,
            resolvedAt: tag.resolvedAt,
            isSelfResolved: tag.isSelfResolved,
          }}
        />
      ))}
      <Popover open={tagsOpen} onOpenChange={setTagsOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Icon name="Plus" size="auto" />
            Add
          </Button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" className="p-0">
          <Command>
            <CommandInput placeholder="Select tag" />
            <CommandList>
              <CommandGroup>
                {availableTagOptions.map((t) => (
                  <CommandItem
                    key={t.tag}
                    onSelect={() => handleAddAppointmentTag(t.id)}
                  >
                    {formatAppointmentTagLabel(t.tag)}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </Stack>
  );
};
